import React, { ReactElement, useCallback, useMemo } from "react";
import { QualityHubActionFormProps } from "./QualityHubActionForm.types";
import { FormSection, FormControl } from "src/ui/components/Forms";
import { TextField, Dropdown, IDropdownOption, Checkbox } from "@fluentui/react";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { Conditional } from "src/components/Conditional";

/**
 * QualityHubActionForm component
 *
 * @param {QualityHubActionFormProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubActionForm({
    canChangeContentStatus = false,
    canRequireVerification = false,
    model,
    onModelChange,
}: QualityHubActionFormProps): ReactElement | null {
    const l10n = useMemo(() => ({
        description: localize("Beskrivning"),
        title: localize("Titel"),
    }), []);

    const onTitleChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                title: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onRequireVerificationChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                requireVerification: !!checked,
                verificationType: !checked
                    ? SpintrTypes.QualityHubVerificationType.None
                    : SpintrTypes.QualityHubVerificationType.Image,
            }));
        }, [onModelChange]);

    const onDescriptionChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                description: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onPublishedChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                contentStatus: checked
                    ? SpintrTypes.ContentStatus.Published
                    : SpintrTypes.ContentStatus.Draft,
            }));
        }, [onModelChange]);

    return (
        <div className="QualityHubActionForm">
            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.title}
                               name="title"
                               onChange={onTitleChanged}
                               required={true}
                               value={model.title} />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <TextField aria-required={false}
                               className="textField"
                               label={l10n.description}
                               multiline={true}
                               name="description"
                               onChange={onDescriptionChanged}
                               required={false}
                               value={model.description} />
                </FormControl>
            </FormSection>

            <Conditional condition={canChangeContentStatus}>

                <FormSection title={localize("Status")}>
                    <Checkbox checked={model.contentStatus === SpintrTypes.ContentStatus.Published}
                              label={localize("Publicerad")}
                              name="contentStatus"
                              onChange={onPublishedChanged} />
                </FormSection>
            </Conditional>

            <Conditional condition={canRequireVerification}>

                <FormSection title={localize("QH_VERIFICATION")}>
                    <Checkbox checked={model.requireVerification}
                              label={localize("QH_REQUIRE_VERIFICATION")}
                              name="requireVerification"
                              onChange={onRequireVerificationChanged} />
                </FormSection>

            </Conditional>
        </div>
    );
}

export default QualityHubActionForm;

